import * as THREE from "three";
import { AppContext } from "../appContext";

export interface PanelLayout {
  entityPosition(id: string): THREE.Vector3 | null;
  projectToScreen(pos: THREE.Vector3): { x: number; y: number };
  projForNode(id: string): { x: number; y: number } | null;
  projForNodeLive(id: string): { x: number; y: number } | null;
  projForNodeLiveRaised(id: string, worldUp: number): { x: number; y: number } | null;
}

/**
 * World→screen projection window. Owns the only `entityPosition` /
 * `projectToScreen` / `projForNode` window other modules (ports, secondary
 * panels, overlay) read through. The object/emitter panel itself is pinned
 * to the top-right corner by CSS (.panel-object) and never follows the
 * selection.
 */
export function initPanelLayout(ctx: AppContext): PanelLayout {
  const { field, emitterField, rig } = ctx;

  function entityPosition(id: string): THREE.Vector3 | null {
    for (const o of field.list()) {
      if (o.id === id) {
        return o.position;
      }
    }
    for (const e of emitterField.list()) {
      if (e.id === id) {
        return e.position;
      }
    }
    return null;
  }

  function projectToScreen(pos: THREE.Vector3): { x: number; y: number } {
    const ndc = pos.clone().project(rig.activeCamera);
    return { x: ((ndc.x + 1) / 2) * window.innerWidth, y: ((1 - ndc.y) / 2) * window.innerHeight };
  }

  function projForNode(id: string): { x: number; y: number } | null {
    const p = entityPosition(id);
    return p ? projectToScreen(p) : null;
  }

  function projForNodeLive(id: string): { x: number; y: number } | null {
    const p = field.liveOrBasePosition(id) ?? emitterField.liveOrBasePosition(id);
    return p ? projectToScreen(p) : null;
  }

  /**
   * Screen projection of a point `worldUp` units above the entity's live
   * position along world +Y (screen-up in the dimension-locked 2D view). Lets the
   * on-object ports overlay anchor clear of the gizmo's move handle, which
   * reaches that far up from the entity center.
   */
  function projForNodeLiveRaised(id: string, worldUp: number): { x: number; y: number } | null {
    const p = field.liveOrBasePosition(id) ?? emitterField.liveOrBasePosition(id);
    return p ? projectToScreen(p.clone().setY(p.y + worldUp)) : null;
  }

  return { entityPosition, projectToScreen, projForNode, projForNodeLive, projForNodeLiveRaised };
}
